/**
 * Whose turn is it, and is the reply taking too long? Drives the turn line on
 * both training pages ("AI Speaking", "Your Turn to Speak", "Processing...",
 * and the slow copies) from the audio the client already has, with no server
 * protocol: see utils/turnIndicatorState for the state machine.
 *
 * Customer playback is modelled arithmetically, never read from the audio
 * graph: gpt-live streams PCM16 24 kHz mono, and every chunk pushes
 * `playbackEndsAt` out by its own duration. Near-silent frames occupy the
 * queue but are not events: gpt-live streams them between utterances, and
 * the mic streams silence continuously. One timeout at a time, no polling.
 *
 * Display only: it gates, sends and ends nothing. The "no end-of-turn timer"
 * rule on the training pages is about turn-taking, which stays with the voice
 * model and the server; REP_TURN_END_MS only chooses a label.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { hasAudibleVoice, REP_VOICE_LEVEL } from '../utils/customerVoice';
import { INITIAL_TURN, TurnEvent, TurnState, transitionTurn } from '../utils/turnIndicatorState';

/** gpt-live's output format: 24 kHz, 16-bit (2-byte) mono samples. */
const CUSTOMER_AUDIO_BYTES_PER_MS = (24000 * 2) / 1000;

export interface TurnIndicator {
  turn: TurnState;
  /** Call for every customer audio chunk, next to playbookLiveAudio.playChunk. */
  noteCustomerAudio: (pcm: ArrayBuffer) => void;
  /** Call for every microphone chunk, next to sending it. */
  noteMicAudio: (pcm: ArrayBuffer) => void;
  /** Back to "Your Turn", forget the queue, drop the timeout (start, end, teardown). */
  reset: () => void;
}

export function useTurnIndicator(): TurnIndicator {
  const [turn, setTurn] = useState<TurnState>(INITIAL_TURN);
  const turnRef = useRef<TurnState>(INITIAL_TURN);
  const playbackEndsAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const moveTo = useCallback((next: TurnState) => {
    // Only on change, so the aria-live line is not re-announced per chunk.
    if (turnRef.current === next) return;
    turnRef.current = next;
    setTurn(next);
  }, []);

  const apply = useCallback(
    (event: TurnEvent) => {
      const transition = transitionTurn(turnRef.current, event);
      if (!transition) return;
      clearTimer();
      moveTo(transition.turn);
      const { timeout } = transition;
      if (!timeout) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        apply({ type: timeout.event });
      }, timeout.delayMs);
    },
    [clearTimer, moveTo]
  );

  const reset = useCallback(() => {
    clearTimer();
    playbackEndsAtRef.current = 0;
    moveTo(INITIAL_TURN);
  }, [clearTimer, moveTo]);

  const noteCustomerAudio = useCallback(
    (pcm: ArrayBuffer) => {
      const now = performance.now();
      const chunkMs = (pcm?.byteLength ?? 0) / CUSTOMER_AUDIO_BYTES_PER_MS;
      playbackEndsAtRef.current = Math.max(now, playbackEndsAtRef.current) + chunkMs;
      if (!hasAudibleVoice(pcm)) return;
      apply({ type: 'customer_audible', playbackRemainingMs: playbackEndsAtRef.current - now });
    },
    [apply]
  );

  const noteMicAudio = useCallback(
    (pcm: ArrayBuffer) => {
      if (hasAudibleVoice(pcm, REP_VOICE_LEVEL)) apply({ type: 'rep_audible' });
    },
    [apply]
  );

  useEffect(() => clearTimer, [clearTimer]);

  return useMemo(
    () => ({ turn, noteCustomerAudio, noteMicAudio, reset }),
    [turn, noteCustomerAudio, noteMicAudio, reset]
  );
}
